"use client";

import type { Control, FieldPath, FieldValues } from "react-hook-form";
import { useController } from "react-hook-form";
import { Input, Label, cn } from "@fluid-app/ui-primitives";

interface FormTextFieldProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> {
  control: Control<TFieldValues>;
  name: TName;
  label?: string;
  placeholder?: string;
  containerClassName?: string;
  type?: React.HTMLInputTypeAttribute;
  disabled?: boolean;
  inputMode?: React.HTMLAttributes<HTMLInputElement>["inputMode"];
  autoComplete?: string;
}

export function FormTextField<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  control,
  name,
  label,
  placeholder,
  containerClassName,
  type,
  disabled,
  inputMode,
  autoComplete,
}: FormTextFieldProps<TFieldValues, TName>) {
  const {
    field,
    fieldState: { error },
  } = useController({ name, control });

  return (
    <div className={cn("space-y-1", containerClassName)}>
      {label && (
        <Label htmlFor={name} className="mb-1.5 block text-sm font-medium">
          {label}
        </Label>
      )}
      <Input
        {...field}
        value={field.value ?? ""}
        id={name}
        type={type}
        disabled={disabled}
        inputMode={inputMode}
        placeholder={placeholder}
        autoComplete={autoComplete}
        aria-invalid={!!error}
      />
      {error?.message && (
        <p className="text-destructive text-sm">{error.message}</p>
      )}
    </div>
  );
}
